import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import axios from "axios";
import { COMPANY_LOGIN_PATH } from "../config/authPaths";
import {
  currentRouterPath,
  isRootStorefront,
  isStorefrontPath,
  usesHashRouter,
} from "../config/storefrontRouting";
import { sanitizeInputTree } from "../utils/sanitizeInput";

interface ElectronAPI {
  isElectron: boolean;
  getBackendUrl: () => Promise<string>;
  getBackendPort: () => Promise<number>;
  getAppVersion: () => Promise<string>;
  getPlatformInfo: () => Promise<{
    platform: string;
    arch: string;
    version: string;
  }>;
  openExternal: (url: string) => Promise<boolean>;
  toggleDevTools: () => Promise<void>;
  platform: string;
}

declare global {
  interface Window {
    electronAPI?: ElectronAPI;
  }
}

interface FetchDataParams<T = unknown> {
  url: string;
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  data?: T;
  params?: Record<string, unknown>;
  config?: AxiosRequestConfig;
}

type PrincipalType = "user" | "company";

type InternalRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _suppress403?: boolean;
  skipAuthRedirect?: boolean;
};

interface UnauthorizedResponseShape {
  error?: { code?: string };
}

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}

function isUnauthorizedError(error: AxiosError): boolean {
  if (error.response?.status === 401) return true;
  const data = error.response?.data as UnauthorizedResponseShape | undefined;
  return data?.error?.code === "UNAUTHORIZED";
}

function isAuthEndpoint(url = ""): boolean {
  return [
    "/auth/login",
    "/auth/register",
    "/auth/website/login",
    "/auth/website/register",
    "/auth/refresh",
    "/auth/forgot-password",
    "/auth/reset-password",
    "/auth/verify-email",
    "/auth/resend-verification",
    "/auth/verify-email-otp",
    "/auth/resend-email-otp",
    "/companies/login",
    "/companies/refresh",
  ].some((endpoint) => url.includes(endpoint));
}

class APIService {
  private baseApi: AxiosInstance;
  private apiBaseUrl: string;
  private refreshPromise: Promise<string> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private isHandlingSessionExpiry = false;
  private pendingGets = new Map<string, Promise<AxiosResponse>>();

  constructor() {
    this.apiBaseUrl = this.getDefaultBaseUrl();
    this.baseApi = axios.create({
      baseURL: this.apiBaseUrl,
      timeout: 15_000,
      headers: { "Content-Type": "application/json" },
      paramsSerializer: {
        serialize: (params) => this.serializeParams(params),
      },
    });
    this.installInterceptors();
    this.initProactiveRefresh();
    void this.resolveElectronBackendUrl();
  }

  private normalizeApiBaseUrl(url?: string): string {
    if (!url) return "";
    return url.endsWith("/") ? url : `${url}/`;
  }

  private getDefaultBaseUrl(): string {
    const env = import.meta.env as { VITE_API_BASE_URL?: string };
    return this.normalizeApiBaseUrl(
      env.VITE_API_BASE_URL || "http://localhost:4000/api/",
    );
  }

  private installInterceptors(): void {
    this.baseApi.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // The instance defaults to JSON. Keeping that header for FormData makes
        // Axios serialize files as `{}` instead of sending multipart bytes.
        if (config.data instanceof FormData) {
          config.headers.delete("Content-Type");
        } else if (config.data && typeof config.data === "object") {
          config.data = sanitizeInputTree(config.data, "html-aware");
        }
        if (config.params && typeof config.params === "object") {
          config.params = sanitizeInputTree(config.params, "plain");
        }
        if (!config.headers.has("Authorization")) {
          const accessToken = localStorage.getItem("accessToken");
          if (accessToken)
            config.headers.set("Authorization", `Bearer ${accessToken}`);
        }

        if (!config.headers.has("Accept-Language")) {
          const language =
            localStorage.getItem("language") || document.documentElement.lang;
          config.headers.set(
            "Accept-Language",
            language === "ar" ? "ar" : "en",
          );
        }
        const currentPath = currentRouterPath();
        if (
          !config.headers.has("x-tenant-path") &&
          isStorefrontPath(currentPath)
        ) {
          config.headers.set(
            "x-tenant-path",
            isRootStorefront() ? "/" : currentPath,
          );
        }
        return config;
      },
      (error: AxiosError) => Promise.reject(error),
    );

    this.baseApi.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const config = error.config as InternalRequestConfig | undefined;

        if (
          error.response?.status === 403 &&
          config &&
          !isAuthEndpoint(config.url)
        ) {
          if (!config._suppress403) {
            const body = error.response.data as { message?: string | string[] };
            const rawMessage = body?.message;
            const message = Array.isArray(rawMessage)
              ? rawMessage.join(". ")
              : rawMessage ||
                "You do not have permission to perform this action.";
            window.dispatchEvent(
              new CustomEvent("permission-denied", { detail: message }),
            );
          }
          return Promise.reject(error);
        }

        if (
          !config ||
          !isUnauthorizedError(error) ||
          isAuthEndpoint(config.url)
        ) {
          return Promise.reject(error);
        }

        const hasCredentials =
          !!localStorage.getItem("accessToken") ||
          !!localStorage.getItem("refreshToken");
        if (!hasCredentials) return Promise.reject(error);

        if (config._retry) {
          if (!config.skipAuthRedirect) await this.handleSessionExpired();
          return Promise.reject(error);
        }

        config._retry = true;
        try {
          const accessToken = await this.getRefreshedAccessToken();
          config.headers.set("Authorization", `Bearer ${accessToken}`);
          return this.baseApi.request(config);
        } catch (refreshError) {
          if (!config.skipAuthRedirect) await this.handleSessionExpired();
          return Promise.reject(refreshError);
        }
      },
    );
  }

  private getRefreshedAccessToken(): Promise<string> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.performTokenRefresh()
        .then((tokens) => tokens.accessToken)
        .finally(() => {
          this.refreshPromise = null;
        });
    }
    return this.refreshPromise;
  }

  private async performTokenRefresh(): Promise<RefreshResponse> {
    const refreshToken = localStorage.getItem("refreshToken");
    const principalType = localStorage.getItem(
      "principalType",
    ) as PrincipalType | null;
    if (
      !refreshToken ||
      (principalType !== "user" && principalType !== "company")
    ) {
      throw new Error("No valid refresh session is available");
    }

    const endpoint =
      principalType === "company" ? "companies/refresh" : "auth/refresh";
    const response = await axios.post(
      `${this.apiBaseUrl}${endpoint}`,
      { refreshToken },
      {
        timeout: 15_000,
        headers: {
          "Content-Type": "application/json",
          "Accept-Language":
            localStorage.getItem("language") === "ar" ||
            document.documentElement.lang === "ar"
              ? "ar"
              : "en",
        },
      },
    );
    const tokens = (response.data?.data ??
      response.data) as Partial<RefreshResponse>;
    if (!tokens.accessToken || !tokens.refreshToken) {
      throw new Error("Invalid token refresh response");
    }

    localStorage.setItem("accessToken", tokens.accessToken);
    localStorage.setItem("refreshToken", tokens.refreshToken);
    this.scheduleRefreshFromToken(tokens.accessToken);
    return tokens as RefreshResponse;
  }

  private initProactiveRefresh(): void {
    if (typeof localStorage === "undefined") return;
    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");
    if (accessToken && refreshToken) this.scheduleRefreshFromToken(accessToken);
  }

  private scheduleRefreshFromToken(accessToken: string): void {
    this.clearRefreshTimer();
    const expiresAt = this.readJwtExpiry(accessToken);
    if (!expiresAt) return;

    this.scheduleRefreshIn(Math.max(expiresAt - Date.now() - 60_000, 5_000));
  }

  scheduleProactiveRefresh(expiresInSeconds: number): void {
    const safeExpiresIn =
      Number.isFinite(expiresInSeconds) && expiresInSeconds > 0
        ? expiresInSeconds
        : 300;
    const bufferSeconds = safeExpiresIn > 120 ? 60 : safeExpiresIn * 0.25;
    this.scheduleRefreshIn(
      Math.max((safeExpiresIn - bufferSeconds) * 1_000, 5_000),
    );
  }

  private scheduleRefreshIn(delay: number): void {
    this.clearRefreshTimer();
    this.refreshTimer = setTimeout(() => {
      if (!localStorage.getItem("refreshToken")) return;
      void this.getRefreshedAccessToken().catch(() => {
        // A later protected request will perform normal session-expiry handling.
      });
    }, delay);
  }

  private readJwtExpiry(token: string): number | null {
    try {
      const payload = token.split(".")[1];
      if (!payload) return null;
      const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
      const decoded = JSON.parse(atob(normalized)) as { exp?: number };
      return typeof decoded.exp === "number" ? decoded.exp * 1_000 : null;
    } catch {
      return null;
    }
  }

  clearRefreshTimer(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  private async handleSessionExpired(): Promise<void> {
    if (this.isHandlingSessionExpiry) return;
    this.isHandlingSessionExpiry = true;

    try {
      const [{ default: store }, session, { queryClient }] = await Promise.all([
        import("../redux/store"),
        import("./sessionService"),
        import("../lib/queryClient"),
      ]);
      session.clearFrontendSession(store.dispatch, queryClient);
    } catch {
      this.clearRefreshTimer();
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("principalType");
      localStorage.removeItem("roleType");
    }
    sessionStorage.setItem("session_expired", "true");

    const currentPath = currentRouterPath();
    if (isStorefrontPath(currentPath)) {
      window.dispatchEvent(new CustomEvent("website:auth-required"));
      this.isHandlingSessionExpiry = false;
    } else if (usesHashRouter()) {
      if (window.location.hash !== `#${COMPANY_LOGIN_PATH}`) {
        window.location.hash = COMPANY_LOGIN_PATH;
      }
    } else if (window.location.pathname !== COMPANY_LOGIN_PATH) {
      window.location.assign(COMPANY_LOGIN_PATH);
    }
  }

  private serializeParams(params?: Record<string, unknown>): string {
    if (!params) return "";
    const searchParams = new URLSearchParams();
    Object.keys(params)
      .sort()
      .forEach((key) => {
        const value = params[key];
        const values = Array.isArray(value) ? value : [value];
        values.forEach((item) => {
          if (item !== undefined && item !== null) {
            searchParams.append(key, String(item));
          }
        });
      });
    return searchParams.toString();
  }

  private getDedupKey(url: string, config: AxiosRequestConfig): string {
    const headers = config.headers as Record<string, unknown> | undefined;
    const tenant =
      headers?.["x-tenant-path"] ?? headers?.["X-Tenant-Path"] ?? "";
    const language =
      headers?.["Accept-Language"] ?? localStorage.getItem("language") ?? "en";
    return `${url}?${this.serializeParams(config.params)}|tenant=${String(tenant)}|lang=${String(language)}`;
  }

  private deduplicatedGet(
    url: string,
    config: AxiosRequestConfig = {},
  ): Promise<AxiosResponse> {
    const key = this.getDedupKey(url, config);
    const pending = this.pendingGets.get(key);
    if (pending) return pending;

    const request = this.baseApi.get(url, config).finally(() => {
      if (this.pendingGets.get(key) === request) this.pendingGets.delete(key);
    });
    this.pendingGets.set(key, request);
    return request;
  }

  private async resolveElectronBackendUrl(): Promise<void> {
    if (!window.electronAPI?.isElectron || !window.electronAPI.getBackendUrl)
      return;
    try {
      const url = await Promise.race([
        window.electronAPI.getBackendUrl(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("IPC timeout")), 3_000),
        ),
      ]);
      const normalized = this.normalizeApiBaseUrl(url);
      if (normalized && normalized !== this.apiBaseUrl) {
        this.apiBaseUrl = normalized;
        this.baseApi.defaults.baseURL = normalized;
      }
    } catch (error) {
      console.warn("[API] Could not get backend URL from Electron:", error);
    }
  }

  resolveAssetUrl(url: string | null | undefined): string | null | undefined {
    if (!url || /^(?:https?:|data:|blob:)/i.test(url)) return url;
    const fallback =
      typeof window === "undefined" ? "http://localhost" : window.location.href;
    const origin = new URL(this.baseApi.defaults.baseURL ?? fallback, fallback)
      .origin;
    return new URL(url, `${origin}/`).toString();
  }

  fetchData(param: FetchDataParams = { url: "" }): Promise<unknown> {
    const method = (param.method ?? "GET").toUpperCase();
    if (method === "GET") {
      return this.get(param.url, {
        ...param.config,
        params: param.params ?? param.config?.params,
      }).then((response) => response.data);
    }
    return this.baseApi
      .request({
        url: param.url,
        method,
        data: param.data,
        params: param.params,
        ...param.config,
      })
      .then((response: AxiosResponse) => response.data);
  }

  get(url: string, config: AxiosRequestConfig = {}): Promise<AxiosResponse> {
    return this.deduplicatedGet(url, config);
  }

  post(
    url: string,
    data?: unknown,
    config: AxiosRequestConfig = {},
  ): Promise<AxiosResponse> {
    return this.baseApi.post(url, data, config);
  }

  put(
    url: string,
    data?: unknown,
    config: AxiosRequestConfig = {},
  ): Promise<AxiosResponse> {
    return this.baseApi.put(url, data, config);
  }

  patch(
    url: string,
    data?: unknown,
    config: AxiosRequestConfig = {},
  ): Promise<AxiosResponse> {
    return this.baseApi.patch(url, data, config);
  }

  delete(url: string, config: AxiosRequestConfig = {}): Promise<AxiosResponse> {
    return this.baseApi.delete(url, config);
  }
}

const api = new APIService();

export function resolveApiAssetUrl(
  url: string | null | undefined,
): string | null | undefined {
  return api.resolveAssetUrl(url);
}

export default api;
